import type { Conversation } from "~/ai-chat/model/conversation";

export interface ConversationGroup {
  label: string;
  conversations: Conversation[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function ordinal(day: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = day % 100;
  return `${day}${suffixes[(value - 20) % 10] ?? suffixes[value] ?? suffixes[0]}`;
}

// "Aug 11th" for the calendar day `daysAgo` before `now`.
function formatDay(now: Date, daysAgo: number): string {
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysAgo);
  return `${MONTHS[date.getMonth()]} ${ordinal(date.getDate())}`;
}

// Buckets conversations into recency groups (newest first) for the session-list design,
// with date-range labels ("Today – Aug 11th", "7 days ago – Aug 9th to Aug 4th"). Empty
// buckets are omitted so the list only shows headings it has rows for.
export function groupConversationsByRecency(conversations: Conversation[], now: Date): ConversationGroup[] {
  const today = startOfDay(now);
  const buckets = [
    { label: `Today – ${formatDay(now, 0)}`, maxDaysAgo: 0, items: [] as Conversation[] },
    { label: `Yesterday – ${formatDay(now, 1)}`, maxDaysAgo: 1, items: [] as Conversation[] },
    { label: `7 days ago – ${formatDay(now, 2)} to ${formatDay(now, 7)}`, maxDaysAgo: 7, items: [] as Conversation[] },
    {
      label: `1 month ago – ${formatDay(now, 8)} to ${formatDay(now, 30)}`,
      maxDaysAgo: 30,
      items: [] as Conversation[],
    },
  ];
  const older: Conversation[] = [];

  const sorted = [...conversations].sort((a, b) => b.updated.getTime() - a.updated.getTime());
  for (const conversation of sorted) {
    const daysAgo = Math.round((today - startOfDay(conversation.updated)) / DAY_MS);
    const bucket = buckets.find((candidate) => daysAgo <= candidate.maxDaysAgo);
    if (bucket) bucket.items.push(conversation);
    else older.push(conversation);
  }

  const groups = buckets
    .filter((bucket) => bucket.items.length > 0)
    .map((bucket) => ({ label: bucket.label, conversations: bucket.items }));
  if (older.length > 0) groups.push({ label: "Older", conversations: older });
  return groups;
}

// Ordered status buckets for the "Group by → Status" filter. A null status (older
// conversations the backend never stamped) folds into "Completed". Empty buckets
// are omitted so only headings with rows are shown.
const STATUS_ORDER = [
  { key: "running", label: "Processing" },
  { key: "error", label: "Error" },
  { key: "aborted", label: "Stopped" },
  { key: "completed", label: "Completed" },
] as const;

export function groupConversationsByStatus(
  conversations: Conversation[],
  runningIds: ReadonlySet<string> = new Set(),
): ConversationGroup[] {
  const sorted = [...conversations].sort((a, b) => b.updated.getTime() - a.updated.getTime());
  // A live stream outranks the cached status, matching the row dot — otherwise a
  // conversation streaming now (cached "completed") would sort under Completed.
  const effectiveStatus = (conversation: Conversation) =>
    runningIds.has(conversation.id) ? "running" : (conversation.status ?? "completed");
  return STATUS_ORDER.map((bucket) => ({
    label: bucket.label,
    conversations: sorted.filter((conversation) => effectiveStatus(conversation) === bucket.key),
  })).filter((group) => group.conversations.length > 0);
}
